/**
 * Size guide modal — open/close plus the IN/CM unit toggle.
 *
 * The open trigger lives in the variant picker, outside this element, so that
 * click is delegated from the document and torn down in `disconnectedCallback`.
 */
if (!customElements.get('size-chart-modal')) {
  class SizeChartModal extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onUnitClick = this.onUnitClick.bind(this);
    }

    connectedCallback() {
      this.setupEventListeners();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.close();
    }

    setupEventListeners() {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);

      for (const button of this.querySelectorAll('.size-chart-unit')) {
        button.addEventListener('click', this.onUnitClick);
      }
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get description() {
      return this.querySelector('.size-chart-modal__product-desc');
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('size-chart-open');
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('size-chart-open');
    }

    /**
     * The trigger sits in the variant picker, so both open and close are handled
     * from one delegated listener.
     * @param {MouseEvent} event
     */
    onDocumentClick(event) {
      if (event.target.closest('[data-size-chart-open]')) {
        event.stopPropagation();
        this.open();
        return;
      }

      const closer = event.target.closest('[data-size-chart-close]');
      if (closer && this.contains(closer)) this.close();
    }

    /**
     * @param {KeyboardEvent} event
     */
    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) this.close();
    }

    /**
     * @param {MouseEvent} event
     */
    onUnitClick(event) {
      const button = event.currentTarget;

      for (const other of this.querySelectorAll('.size-chart-unit')) {
        other.classList.remove('active');
      }
      button.classList.add('active');

      this.convertTable(button.dataset.unit);
      this.convertDescription(button.dataset.unit);
    }

    /**
     * @param {string} unit - 'in' or 'cm'
     */
    convertTable(unit) {
      for (const cell of this.querySelectorAll('.size-chart-modal__table td:not(:first-child)')) {
        const original = cell.dataset.originalValue;

        if (unit !== 'cm') {
          cell.textContent = original;
          continue;
        }

        // Live parses the leading number only, so a range like "30 - 33"
        // collapses to a single cm value ("76"). Matched deliberately.
        const value = parseFloat(original);
        if (!isNaN(value)) cell.textContent = (Math.round(value * 2.54 * 2) / 2).toString();
      }
    }

    /**
     * @param {string} unit - 'in' or 'cm'
     */
    convertDescription(unit) {
      const description = this.description;
      if (!description) return;

      description.textContent =
        unit === 'cm'
          ? description.textContent.replace('inches', 'centimeters')
          : description.textContent.replace('centimeters', 'inches');
    }
  }

  customElements.define('size-chart-modal', SizeChartModal);
}
